import axios, { AxiosInstance, AxiosError } from 'axios';

// Types
export interface User {
  id: string;
  username: string;
  email: string;
  role: 'admin' | 'user';
  is_active: boolean;
  is_password_set: boolean;
  expiration_date: string | null;
  created_at: string;
  updated_at: string;
  last_login: string | null;
}

export interface LoginResponse {
  user: User;
  access_token: string;
  token_type: string;
}

export interface InviteValidationResponse {
  valid: boolean;
  username?: string;
  email?: string;
  message?: string;
}

export interface SetPasswordResponse {
  success: boolean;
  message: string;
}

// Get API base URL
const getApiBaseUrl = (): string => {
  if (process.env.REACT_APP_API_URL) {
    return process.env.REACT_APP_API_URL;
  }
  const hostname = window.location.hostname;
  if (hostname !== 'localhost' && hostname !== '127.0.0.1') {
    return `http://${hostname}:9001/api/v1`;
  }
  return '/api/v1';
};

// Create axios instance with credentials
const authClient: AxiosInstance = axios.create({
  baseURL: getApiBaseUrl(),
  withCredentials: true, // Important: send cookies with requests
  headers: {
    'Content-Type': 'application/json',
  },
  timeout: 30000,
});

// Token refresh state
let isRefreshing = false;
let failedQueue: Array<{
  resolve: (value?: unknown) => void;
  reject: (reason?: unknown) => void;
}> = [];

const processQueue = (error: AxiosError | null = null): void => {
  failedQueue.forEach((prom) => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve();
    }
  });
  failedQueue = [];
};

// Response interceptor for handling 401 and token refresh
authClient.interceptors.response.use(
  (response) => response,
  async (error: AxiosError) => {
    const originalRequest = error.config as typeof error.config & {
      _retry?: boolean;
    };

    // Skip refresh for login, invite, and auth check endpoints
    const skipRefreshPaths = ['/auth/login', '/auth/invite', '/auth/refresh', '/auth/me'];
    const shouldSkipRefresh = skipRefreshPaths.some((path) =>
      originalRequest?.url?.includes(path)
    );

    if (
      error.response?.status === 401 &&
      !originalRequest?._retry &&
      !shouldSkipRefresh
    ) {
      if (isRefreshing) {
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        }).then(() => authClient(originalRequest!));
      }

      originalRequest!._retry = true;
      isRefreshing = true;

      try {
        await authAPI.refreshToken();
        processQueue();
        return authClient(originalRequest!);
      } catch (refreshError) {
        processQueue(refreshError as AxiosError);
        // Clear auth state and redirect to login
        window.location.href = '/login';
        return Promise.reject(refreshError);
      } finally {
        isRefreshing = false;
      }
    }

    return Promise.reject(error);
  }
);

// Auth API functions
export const authAPI = {
  async login(username: string, password: string): Promise<LoginResponse> {
    const formData = new URLSearchParams();
    formData.append('username', username);
    formData.append('password', password);

    const response = await authClient.post<LoginResponse>(
      '/auth/login',
      formData,
      {
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
      }
    );
    return response.data;
  },

  async logout(): Promise<void> {
    await authClient.post('/auth/logout');
  },

  async refreshToken(): Promise<void> {
    await authClient.post('/auth/refresh');
  },

  async getCurrentUser(): Promise<User> {
    const response = await authClient.get<User>('/auth/me');
    return response.data;
  },

  async changePassword(
    currentPassword: string,
    newPassword: string
  ): Promise<{ message: string }> {
    const response = await authClient.post<{ message: string }>(
      '/auth/change-password',
      {
        current_password: currentPassword,
        new_password: newPassword,
      }
    );
    return response.data;
  },

  // Invite flow
  async validateInvite(token: string): Promise<InviteValidationResponse> {
    const response = await authClient.get<InviteValidationResponse>(
      `/auth/invite/${token}/validate`
    );
    return response.data;
  },

  async completeInvite(
    token: string,
    password: string,
    confirmPassword: string
  ): Promise<SetPasswordResponse> {
    const response = await authClient.post<SetPasswordResponse>(
      `/auth/invite/${token}/complete`,
      {
        password,
        confirm_password: confirmPassword,
      }
    );
    return response.data;
  },
};

// Export the axios client for use in other API services
export { authClient };
export default authAPI;
